<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Event } from '@/types/events'

const props = defineProps<{
  event: Event
  orgSlug: string | null
}>()

const { t } = useI18n()

const locationOrigin = window.location.origin
const locationHost = window.location.host

const copiedKey = ref<string | null>(null)

async function copyUrl(url: string, key: string): Promise<void> {
  await navigator.clipboard.writeText(url)
  copiedKey.value = key
  setTimeout(() => {
    if (copiedKey.value === key) copiedKey.value = null
  }, 2000)
}

const registrationUrl = computed<string | null>(() => {
  if (!props.event.active_edition) return null
  return props.orgSlug
    ? `${locationOrigin}/${props.orgSlug}/${props.event.slug}`
    : `${locationOrigin}/${props.event.slug}`
})

const registrationUrlDisplay = computed<string | null>(() => {
  if (!props.event.active_edition) return null
  return props.orgSlug
    ? `${locationHost}/${props.orgSlug}/${props.event.slug}`
    : `${locationHost}/${props.event.slug}`
})

const registrationActive = computed<boolean>(() =>
  (props.event.active_edition?.races ?? []).some((r) => r.registrations_open),
)

const volunteerUrl = computed<string | null>(() => {
  if (!props.event.active_edition?.volunteers_enabled) return null
  return props.orgSlug
    ? `${locationOrigin}/${props.orgSlug}/${props.event.slug}/volunteer`
    : `${locationOrigin}/${props.event.slug}/volunteer`
})

const volunteerUrlDisplay = computed<string | null>(() => {
  if (!props.event.active_edition?.volunteers_enabled) return null
  return props.orgSlug
    ? `${locationHost}/${props.orgSlug}/${props.event.slug}/volunteer`
    : `${locationHost}/${props.event.slug}/volunteer`
})

const eventWebsiteUrl = computed<string>(() => `https://${props.event.slug}.startline.be`)
const eventWebsiteUrlDisplay = computed<string>(() => `${props.event.slug}.startline.be`)
</script>

<template>
  <div class="space-y-1.5">
    <!-- Registration page -->
    <div class="flex items-center gap-2 min-w-0">
      <svg class="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
      <span class="text-xs text-slate-500 shrink-0 w-28">{{ t('events.public_links.registration_page') }}</span>

      <template v-if="registrationUrl">
        <a
          :href="registrationUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-slate-400 hover:text-emerald-400 transition-colors truncate min-w-0 flex-1"
          :title="registrationUrl"
        >
          {{ registrationUrlDisplay }}
        </a>
        <span
          class="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
          :class="registrationActive
            ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
            : 'bg-slate-700/50 border border-slate-600 text-slate-400'"
        >
          <span class="w-1.5 h-1.5 rounded-full" :class="registrationActive ? 'bg-emerald-400' : 'bg-slate-500'" aria-hidden="true" />
          {{ registrationActive ? t('events.public_links.status_live') : t('events.public_links.status_closed') }}
        </span>
        <button
          type="button"
          class="shrink-0 w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          :title="copiedKey === 'reg' ? t('events.public_links.copied') : t('events.public_links.copy')"
          @click="copyUrl(registrationUrl, 'reg')"
        >
          <svg v-if="copiedKey === 'reg'" class="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
      </template>
      <span v-else class="text-xs text-slate-600 italic">{{ t('events.public_links.status_not_configured') }}</span>
    </div>

    <!-- Volunteer signup -->
    <div class="flex items-center gap-2 min-w-0">
      <svg class="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      <span class="text-xs text-slate-500 shrink-0 w-28">{{ t('events.public_links.volunteer_signup') }}</span>

      <template v-if="volunteerUrl">
        <a
          :href="volunteerUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-slate-400 hover:text-emerald-400 transition-colors truncate min-w-0 flex-1"
          :title="volunteerUrl"
        >
          {{ volunteerUrlDisplay }}
        </a>
        <span class="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
          {{ t('events.public_links.status_active') }}
        </span>
        <button
          type="button"
          class="shrink-0 w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          :title="copiedKey === 'vol' ? t('events.public_links.copied') : t('events.public_links.copy')"
          @click="copyUrl(volunteerUrl, 'vol')"
        >
          <svg v-if="copiedKey === 'vol'" class="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
      </template>
      <template v-else-if="!event.active_edition">
        <span class="text-xs text-slate-600 italic">{{ t('events.public_links.status_not_configured') }}</span>
      </template>
      <template v-else>
        <span class="text-xs text-slate-600 italic">{{ t('events.public_links.status_disabled') }}</span>
      </template>
    </div>

    <!-- Event website -->
    <div class="flex items-center gap-2 min-w-0">
      <svg class="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span class="text-xs text-slate-500 shrink-0 w-28">{{ t('events.public_links.event_website') }}</span>
      <a
        :href="eventWebsiteUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="text-xs text-slate-400 hover:text-emerald-400 transition-colors truncate min-w-0 flex-1"
        :title="eventWebsiteUrl"
      >
        {{ eventWebsiteUrlDisplay }}
      </a>
      <span
        class="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
        :class="event.website_enabled
          ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
          : 'bg-slate-700/50 border border-slate-600 text-slate-400'"
      >
        <span class="w-1.5 h-1.5 rounded-full" :class="event.website_enabled ? 'bg-emerald-400' : 'bg-slate-500'" aria-hidden="true" />
        {{ event.website_enabled ? t('events.public_links.status_live') : t('events.public_links.status_offline') }}
      </span>
      <button
        type="button"
        class="shrink-0 w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
        :title="copiedKey === 'web' ? t('events.public_links.copied') : t('events.public_links.copy')"
        @click="copyUrl(eventWebsiteUrl, 'web')"
      >
        <svg v-if="copiedKey === 'web'" class="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        <svg v-else class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </button>
    </div>
  </div>
</template>
